import { Controller } from "@hotwired/stimulus"

const QUERIES_KEY = id => `hunt-queries-${id}`
const NOTES_KEY = id => `hunt-notes-${id}`
const VERDICT_KEY = id => `hunt-verdict-${id}`

export default class extends Controller {
  static targets = [
    "list", "detail", "detailTitle", "detailDesc", "queryList", "findingsList",
    "notepad", "notesSaved", "verdictSelect", "createForm", "nameInput", "descInput",
    "formStatus", "emptyState",
  ]
  static values = { url: String, logsUrl: String }

  #hunts = []
  #activeHuntId = null

  connect() {
    this.#loadHunts()
  }

  showCreateForm() {
    this.createFormTarget.classList.remove("hidden")
    this.nameInputTarget.focus()
    if (this.hasFormStatusTarget) this.formStatusTarget.textContent = ""
  }

  hideCreateForm() {
    this.createFormTarget.classList.add("hidden")
  }

  async createHunt(event) {
    event.preventDefault()
    const name = this.nameInputTarget.value.trim()
    if (!name) { this.nameInputTarget.focus(); return }

    const description = this.descInputTarget.value.trim()
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""

    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        body: new URLSearchParams({ "investigation[name]": name, "investigation[description]": description, authenticity_token: csrf }),
        headers: { Accept: "application/json" },
      })
      if (resp.ok) {
        this.nameInputTarget.value = ""
        this.descInputTarget.value = ""
        this.hideCreateForm()
        await this.#loadHunts()
      } else {
        if (this.hasFormStatusTarget) this.formStatusTarget.textContent = "Save failed"
      }
    } catch {
      if (this.hasFormStatusTarget) this.formStatusTarget.textContent = "Save failed"
    }
  }

  openHunt(event) {
    const id = event.currentTarget.dataset.huntId
    const hunt = this.#hunts.find(h => String(h.id) === String(id))
    if (!hunt) return
    this.#activeHuntId = id
    this.#renderDetail(hunt)
  }

  closeDetail() {
    this.#activeHuntId = null
    if (this.hasDetailTarget) this.detailTarget.classList.add("hidden")
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
  }

  addQueryToHunt() {
    if (!this.#activeHuntId) return
    const query = document.querySelector("textarea[name='query']")?.value?.trim() || ""
    if (!query) return
    const queries = this.#getHuntQueries(this.#activeHuntId)
    const label = query.match(/where\s+(.{1,30})/i)?.[1]?.trim() || query.slice(0, 30)
    queries.push({ id: `${Date.now()}`, label, query })
    this.#saveHuntQueries(this.#activeHuntId, queries)
    this.#renderQueryList(queries)
  }

  runQuery(event) {
    const query = event.currentTarget.dataset.query
    if (!query) return
    const textarea = document.querySelector("textarea[name='query']")
    if (textarea) {
      textarea.value = query
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
      const form = textarea.closest("form")
      if (form) form.requestSubmit()
    }
    document.querySelector('[data-mode="analyst"]')?.click()
  }

  removeQuery(event) {
    event.stopPropagation()
    const qid = event.currentTarget.dataset.queryId
    if (!this.#activeHuntId) return
    const queries = this.#getHuntQueries(this.#activeHuntId).filter(q => q.id !== qid)
    this.#saveHuntQueries(this.#activeHuntId, queries)
    this.#renderQueryList(queries)
  }

  syncNotes() {
    if (!this.#activeHuntId) return
    const notes = this.hasNotepadTarget ? this.notepadTarget.value : ""
    const verdict = this.hasVerdictSelectTarget ? this.verdictSelectTarget.value : ""
    localStorage.setItem(NOTES_KEY(this.#activeHuntId), notes)
    localStorage.setItem(VERDICT_KEY(this.#activeHuntId), verdict)
    if (this.hasNotesSavedTarget) {
      this.notesSavedTarget.textContent = "Saved"
      setTimeout(() => { if (this.hasNotesSavedTarget) this.notesSavedTarget.textContent = "" }, 1500)
    }
  }

  async #loadHunts() {
    if (!this.hasListTarget) return
    try {
      const resp = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      this.#hunts = await resp.json()
      this.#renderList()
    } catch {
      this.listTarget.innerHTML = '<div class="siem-hunt-empty">Failed to load hunt sessions.</div>'
    }
  }

  #renderList() {
    if (!this.hasListTarget) return
    if (!this.#hunts.length) {
      this.listTarget.innerHTML = '<div class="siem-hunt-empty">No hunt sessions yet — create one to start.</div>'
      return
    }
    this.listTarget.innerHTML = this.#hunts.map(h => `
      <div class="siem-hunt-item" data-hunt-id="${h.id}"
           data-action="click->accounts--logs--hunt#openHunt">
        <div class="siem-hunt-item-name">${this.#esc(h.name)}</div>
        <div class="siem-hunt-item-meta">
          ${h.log_count} findings · ${this.#formatDate(h.updated_at)}
        </div>
      </div>
    `).join("")
  }

  #renderDetail(hunt) {
    if (!this.hasDetailTarget) return
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    this.detailTarget.classList.remove("hidden")
    this.detailTitleTarget.textContent = hunt.name
    if (this.hasDetailDescTarget) this.detailDescTarget.textContent = hunt.description || ""

    const queries = this.#getHuntQueries(hunt.id)
    this.#renderQueryList(queries)
    this.#renderFindingsList(hunt)

    if (this.hasNotepadTarget) {
      this.notepadTarget.value = localStorage.getItem(NOTES_KEY(hunt.id)) || ""
    }
    if (this.hasVerdictSelectTarget) {
      this.verdictSelectTarget.value = localStorage.getItem(VERDICT_KEY(hunt.id)) || ""
    }
    if (this.hasNotesSavedTarget) this.notesSavedTarget.textContent = ""
  }

  #renderQueryList(queries) {
    if (!this.hasQueryListTarget) return
    if (!queries.length) {
      this.queryListTarget.innerHTML = '<div class="siem-hunt-empty-sm">No queries saved — run a search in Analyst view and click Save Query.</div>'
      return
    }
    this.queryListTarget.innerHTML = queries.map(q => `
      <div class="siem-hunt-query-item">
        <span class="siem-hunt-query-label" title="${this.#esc(q.query)}">${this.#esc(q.label)}</span>
        <button class="siem-hunt-query-btn" title="Re-run in Analyst view"
                data-query="${this.#esc(q.query)}"
                data-action="click->accounts--logs--hunt#runQuery">
          <i class="fa fa-play"></i>
        </button>
        <button class="siem-hunt-query-btn siem-hunt-query-remove" title="Remove"
                data-query-id="${q.id}"
                data-action="click->accounts--logs--hunt#removeQuery">
          <i class="fa fa-times"></i>
        </button>
      </div>
    `).join("")
  }

  #renderFindingsList(hunt) {
    if (!this.hasFindingsListTarget) return
    if (!hunt.log_count) {
      this.findingsListTarget.innerHTML = '<div class="siem-hunt-empty-sm">No logs saved yet — use the log table\'s investigation menu.</div>'
      return
    }
    this.findingsListTarget.innerHTML = `<div class="siem-hunt-findings-count">${hunt.log_count} log ${hunt.log_count === 1 ? "entry" : "entries"} saved to this hunt</div>`
  }

  #getHuntQueries(id) {
    try { return JSON.parse(localStorage.getItem(QUERIES_KEY(id)) || "[]") }
    catch { return [] }
  }

  #saveHuntQueries(id, queries) {
    localStorage.setItem(QUERIES_KEY(id), JSON.stringify(queries))
  }

  #formatDate(dt) {
    try { return new Date(dt).toLocaleDateString() }
    catch { return "" }
  }

  #esc(s) {
    return String(s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
